import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import {
  Dropdown as PrimitiveDropdown,
  DropdownToggle,
  DropdownMenu,
  MenuItem,
} from '@bingads-webui-react/primitive';

/**
 * @param {Object} props props of Dropdown
 * @property {String} id - id of dropdown. Will assign dropdown-{uniqueId} if not provided
 * @property {Array} dropdownOptions - options array to form the dropdown list
 * @property {String|Number|Object} selectedKey - selected option
 * @property {Function} onSelect - callback function when an option is selected
 * @property {Function} renderItem - optional function to render menu items. Use option in dropdownOptions by default
 * @property {Function} renderSelectedItem - optional function to render selected item. Will use renderItem(if passed in) or selectedKey (by default) to render selected Option.
 * @property {Function} isItemDisabled - optional function to indicate whether an option is disabled in the dropdown
 * @property {Bool} enableMenuScroll - option to enable menu scroll when list is longer than 6. Disabled by default.
 * @returns {React.Element} Dropdown react element
*/
export function Dropdown(props) {
  const {
    id,
    selectedOption,
    dropdownOptions,
    onSelect,
    renderItem,
    renderSelectedItem,
    isItemDisabled,
    disabled,
  } = props;

  const renderOptionDisplayed = option => (renderItem ? renderItem(option) : option.toString());
  const renderMenuItem = option => (
    <MenuItem
      className="list-selector-item"
      key={option.id || _.uniqueId()}
      eventKey={option}
      active={_.isEqual(option, selectedOption)}
      disabled={isItemDisabled(option)}
    >
      {renderOptionDisplayed(option)}
    </MenuItem>
  );

  return (
    <PrimitiveDropdown
      id={id}
      onSelect={onSelect}
      disabled={disabled}
    >
      <DropdownToggle>
        {renderSelectedItem ? renderSelectedItem(selectedOption) : renderOptionDisplayed(selectedOption)}
      </DropdownToggle>
      <DropdownMenu className="list-selector-content">
        {_.map(dropdownOptions, option => renderMenuItem(option))}
      </DropdownMenu>
    </PrimitiveDropdown>
  );
}

Dropdown.propTypes = {
  onSelect: PropTypes.func.isRequired,
  id: PropTypes.string,
  selectedOption: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]).isRequired,
  dropdownOptions: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ])).isRequired,
  renderItem: PropTypes.func,
  renderSelectedItem: PropTypes.func,
  isItemDisabled: PropTypes.func,
  disabled: PropTypes.bool,
};

Dropdown.defaultProps = {
  id: _.uniqueId('dropdown-'),
  renderItem: null,
  renderSelectedItem: null,
  isItemDisabled: () => false,
  disabled: false,
};
